class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

class QuantityInputDawn extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input-dawn', QuantityInputDawn);

class ProductVariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {

      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');

      // change previous target.selectedOptions with new value
      const newSelectedOption = Array.from(target.options).find((option) => option.value === value);
      if (newSelectedOption) {
        newSelectedOption.setAttribute('selected', 'selected');
      }

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .product-option__swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('product-option__swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('product-option__swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}
customElements.define('product-variant-selects', ProductVariantSelects);

if (!customElements.get('product-info')) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
        this.productMediaGallery = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        this.productVariantSelects = this.querySelector('product-variant-selects');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true })
        );
        this.initUpdateMedia();

        if (this.productMediaGallery && this.productMediaGallery.dataset.hideOtherVariantsMedia === 'true') {
          this.updateVariantMedia();
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(
          ".product-form__quantity"
        );
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      initUpdateMedia() {
        const variantIdFromUrl = parseInt(window.location.search.split('?').filter(item => item.includes('variant='))[0]?.split('=')[1]) || null;
        if (!variantIdFromUrl) { return; }
        const variant = this.getSelectedVariant(this);
        if (!variant || !variantIdFromUrl) { return; }
        if (variant?.id === variantIdFromUrl) {
          this.updateMedia(variant?.featured_media?.id);
        }

      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push(html =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach(element =>
              element.classList.add("scroll-trigger--cancel")
            )
        );
        this.postProcessHtmlCallbacks.push(newNode => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues }
      }) {
        setTimeout(function(){
          window.__monogramFeature.update();
        }, 700);
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl)
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return html => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(
            html.querySelector(selector)
          );
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then(response => response.text())
          .then(responseText => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch(error => {
            if (error.name === "AbortError") {
              // console.log("Fetch aborted by user");
            } else {
              // console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "product-variant-selects [data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        !shouldFetchFullPage &&
          params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html) {
        const productVariantSelects = html.querySelector("product-variant-selects");
        if (productVariantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            productVariantSelects,
            this.preProcessHtmlCallbacks
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return html => {
          const variant = this.getSelectedVariant(html);

          // this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateVariantDiscountBadge(variant?.price, variant?.compare_at_price);
          this.updateVariantDetails(variant?.id);
          // Inserted logic for pickupAvailability after updateVariantDetails
          if (this.pickupAvailability && variant?.id) {
            this.pickupAvailability.dataset.variantId = variant.id;
            this.pickupAvailability.fetchAvailability(variant.id);
          }

          if (this.productMediaGallery && this.productMediaGallery.dataset.hideOtherVariantsMedia === 'true') {
            this.updateVariantMedia(html);
          }

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(variant?.featured_media?.id);

          const updateSourceFromDestination = (
            id,
            shouldHide = source => false
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);

            if (source && destination) {

              destination.innerHTML = source.innerHTML;

              if (id === 'Inventory') {
                const inventoryThreshold = parseInt(source.dataset.inventoryThreshold);
                const inventoryQuantity = parseInt(source.dataset.inventoryQuantity);
                destination.classList.toggle("hidden", inventoryThreshold && inventoryQuantity > inventoryThreshold || inventoryQuantity < 1);
              } else {
                destination.classList.toggle("hidden", shouldHide(source));
              }
            }
          };

          updateSourceFromDestination("ProductPurchaseInfo");
          updateSourceFromDestination("ProductStickyCart");

          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) => classList.contains("hidden"));

          updateSourceFromDestination("Inventory", ({ innerText }) => innerText === "");

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove("hidden");

          this.productForm?.toggleSubmitButton(
            html
              .getElementById(`ProductSubmitButton-${this.sectionId}`)
              ?.hasAttribute("disabled") ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant
            }
          });
          
        };
      }

      updateVariantMedia() {
        const select_variant_options_handle = this.querySelector(
          'product-variant-selects [data-selected-variant-options-handle]'
        )?.innerHTML.trim();

        if (!select_variant_options_handle) {
          const productAllVariants = this.productVariantSelects.querySelector('[data-all-variants]').innerHTML.trim();

          // Make sure to attach the event listener to the correct element
          this.productVariantSelects.addEventListener('click', (event) => {
            const closestVariantOptionInput = event.target.closest('.product-option__input');

            // Ensure that you are finding the correct input
            if (closestVariantOptionInput) {
              const closestVariantOptionInputValue = closestVariantOptionInput.value;

              // Parse productAllVariants and check for the selected variant
              const availableVariant = JSON.parse(productAllVariants).find(variant => {
                return variant.options.some(option => option === closestVariantOptionInputValue);
              });
            } else {
              // console.log('No variant option input found');
            }
          });
        }

        this.productMediaGallery.querySelectorAll('[data-media-group]').forEach(element => element.classList.add('hidden'));

        this.productMediaGallery.querySelectorAll(`[data-media-group="${select_variant_options_handle}"]`).forEach(element => element.classList.remove('hidden'));

        this.productMediaGallery.querySelectorAll('swiper-product-gallery').forEach(swiperSlide => {
          if (swiperSlide.instance) {
            swiperSlide.instance.update(); // Update the Swiper instance
          } else {
            swiperSlide.init(); // If the instance is not available, reinitialize
          }
        });
      }

      updateVariantOptionMedia() {
        this.productMediaGallery.querySelectorAll('[data-media-group]:not(.hidden)').forEach(element => {

        });
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach(productForm => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${
            variantId ? `?variant=${variantId}` : ""
          }`
        );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`
        );
      }

      updateSellingPlanURL(sellingPlanId, selectedVariantId) {
        if (!selectedVariantId) return;
        if (!sellingPlanId) {
          window.history.replaceState({}, "", `${window.location.href.replace(/&selling_plan=\d+/, "")}`);
        } else {
          const params = new URLSearchParams(window.location.search);
          params[params.has("variant") ? "set" : "append"](
            "variant",
            selectedVariantId
          );
          if (sellingPlanId) {
            params[params.has("selling_plan") ? "set" : "append"](
              "selling_plan",
              sellingPlanId
            );
          } else {
            params.delete("selling_plan");
          }
          window.history.replaceState({}, "", `${this.dataset.url}?${params.toString()}`);
        }
      }

      updateVariantDetails(variantId) {
        const productVariantDetails = this.querySelectorAll('.product__metafield_for_variant-block');
        if (productVariantDetails) {
          productVariantDetails.forEach((productVariantDetail) => {
            const block_id = productVariantDetail.getAttribute('data-block-id');
            const metafieldDataElement = this.querySelector(`#product__metafield_for_variant-block--${block_id}`);
            if (!metafieldDataElement) {
              return;
            }
            try {
              let productVariantDetailsData = JSON.parse(metafieldDataElement?.textContent);
              productVariantDetail.innerHTML = productVariantDetailsData?.[variantId]?.replaceAll('\n', '<br />') || '';
            } catch (error) {
              console.error('Error parsing JSON', error);
            }
          });
        }
      }

      updateVariantDiscountBadge(price, compare_at_price) {
        const discountBadge = this.querySelector(".product-card__badge--discount");
        if (!discountBadge) return;


        if (compare_at_price > price) {
          discountBadge.classList.remove("hidden");
          const percentageElement = discountBadge.querySelector(".percentage");
          // calculate discount percentage
          const discountPercentage = Math.round(
            ((compare_at_price - price) / compare_at_price) * 100
          );
          // update discount percentage
          percentageElement.textContent = discountPercentage;
        } else {
          discountBadge.classList.add("hidden");
        }
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Quantity-Rules"
        ]
          .map(id => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        // if layout is grid, add .active class to the relevant swiper-slide
        const gridLayout = this.querySelector('.main-product__media--grid');
        if (gridLayout) {
          // remove active class from all swiper-slides
          gridLayout.querySelectorAll('.swiper-slide').forEach(item => {
            item.classList.remove('active');
          });
          // add .active class to the swiper-slide with the matching data-media-id
          const activeSlide = gridLayout.querySelector(`.swiper-slide[data-media-id="${variantFeaturedMediaId}"]`);
          if (activeSlide) {
            activeSlide.classList.add('active');
          }
        }

        if (
          this.querySelector(".main-product__media")?.hasAttribute(
            "data-desktop-grid"
          ) && window.innerWidth >= 750
        ) {
          this.scrollToImage(variantFeaturedMediaId);
        }

        const productMedia = this.querySelector(
          "swiper-product-gallery"
        );
        productMedia?.setActiveMedia(variantFeaturedMediaId);
      }

      scrollToImage(variantFeaturedMediaId) {
        this.querySelectorAll("[data-media-id]").forEach(img => {
          if (
            img.getAttribute("data-media-id") ==
            variantFeaturedMediaId
          ) {
            img.scrollIntoView({
              block: "end",
              behavior: "smooth"
            });
          }
        });
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step
            ? parseInt(this.quantityInput.step)
            : 1
        };

        let min = data.min;
        const max =
          data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min)
          min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId =
          this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner"
        ).classList.remove("hidden");
        fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then(response => response.text())
          .then(responseText => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch(e => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner"
            ).classList.add("hidden")
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`
        );
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label"
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step"
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(
          `#ProductModal-${this.dataset.section}`
        );
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("product-variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products"
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list"
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

if (!customElements.get('product-form')) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener(
          "submit",
          this.onSubmitHandler.bind(this)
        );
        this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText =
          this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (
          this.submitButton.getAttribute("aria-disabled") === "true"
        )
          return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove(
          "hidden"
        );

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            "sections",
            this.cart
              .getSectionsToRender()
              .map(section => section.section)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then(response => response.json())
          .then(response => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector(
                ".sold-out-message"
              );
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response
              });
            this.error = false;
            this.cart.renderContents(response);
          })
          .catch(e => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error)
              this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add(
              "hidden"
            );
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute(
          "hidden",
          !errorMessage
        );

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = this.hasAttribute(
            "data-has-selling-plan"
          )
            ? window.variantStrings.addSubscriptionToCart
            : this.submitButtonText.textContent = this.hasAttribute(
                "data-has-pre-order"
              )
            ?  window.variantStrings.preOrder
            : window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    }
  );
}

document.querySelectorAll('.product__content > *').forEach((el) => {
  if (!el.textContent.trim()) {
    el.textContent = '';
  }
});

/* Monogram feature for personalised_monogram products
   - Shows Add Monogram button when product tag exists AND Embroidery option is set to "Personalise"
   - Popup with product image (left) and text/confirm/save/cancel (right)
   - Save persists to localStorage with key productId_variantId
   - Edit/Delete actions supported
   - Integrates with existing hidden input #monogram-hidden-input if present
*/

(function () {
  const STORAGE_PREFIX = 'monogram_v1::'; // versioned storage key
  const MAX_CHARS = 15;
  const PERSONALISE_TAG = 'personalised_monogram';
  const MONOGRAM_CONTAINER_ID = 'monogram-container';
  const HIDDEN_INPUT_ID = 'monogram-hidden-input';
  const POPUP_ROOT_ID = 'monogram-popup-root';

  // Helper: try a variety of ways to determine product tags
  function getProductTags() {
    try {
      const el = document.getElementById("monogram-product-tags");
      if (!el) return [];

      const raw = el.getAttribute("data-tags") || "";
      return raw
        .split(",")
        .map(t => t.trim())
        .filter(Boolean); // remove empty
    } catch (e) {
      console.log("getProductTags error:", e);
      return [];
    }
  }


  // Helper: attempt to get current variant object (several fallbacks)
  function getCurrentVariantFromDOM() {
    // 1. look for element with data-selected-variant (stringified JSON in many themes)
    const selectedVariantEl = document.querySelector("product-variant-selects [data-selected-variant], [data-selected-variant]");
    if (selectedVariantEl && selectedVariantEl.innerHTML) {
      try {
        return JSON.parse(selectedVariantEl.innerHTML);
      } catch (e) { /* continue */ }
    }

    // 2. if product-info has getSelectedVariant available (via its class), call it (safe try)
    try {
      const productInfoEl = document.querySelector('product-info');
      if (productInfoEl && typeof productInfoEl.getSelectedVariant === 'function') {
        const v = productInfoEl.getSelectedVariant(productInfoEl);
        if (v) return v;
      }
    } catch (e) { /* ignore */ }

    // 3. use input[name="id"] value & find variant in window.product
    const idInput = document.querySelector('input[name="id"]');
    if (idInput && window.product && Array.isArray(window.product.variants)) {
      const vid = idInput.value;
      const found = window.product.variants.find(v => String(v.id) === String(vid) || String(v.id) === String(parseInt(vid)));
      if (found) return found;
    }

    // 4. fallback: try to find checked radio/select option's option text (only option values)
    // Build a minimal object with options if possible
    const optionEls = document.querySelectorAll('.product-form__input select, .product-form__input input[type="radio"]');
    if (optionEls.length) {
      // Build an array of selected values
      const selectedValues = [];
      document.querySelectorAll('.product-form__input select').forEach(s => selectedValues.push(s.value));
      document.querySelectorAll('.product-form__input input[type="radio"]:checked').forEach(r => selectedValues.push(r.value));
      // return minimal fallback
      return { id: null, options: selectedValues };
    }

    return null;
  }

  // Return true if product has the required tag(s)
  function productHasPersonalisedTag() {
    const tags = getProductTags();
    return tags.some(t => t.toLowerCase() === PERSONALISE_TAG.toLowerCase());
  }

  // Check whether Embroidery option exists and selected value is Personalise
  function isEmbroideryPersonalised(variant) {
    if (!variant) return false;

    // If variant.options exists and includes 'Personalise' — this is a common case.
    if (Array.isArray(variant.options) && variant.options.some(o => String(o).toLowerCase() === 'personalise')) {
      // Make a stronger check: ensure it's specifically the "Embroidery" option (if optionNames exist)
      // Try to read option names from window.product.options (Shopify JSON)
      if (window.product && Array.isArray(window.product.options) && Array.isArray(variant.options)) {
        // find index of option whose name is 'Embroidery'
        const idx = window.product.options.findIndex(name => String(name).toLowerCase() === 'embroidery');
        if (idx >= 0) {
          return String(variant.options[idx] || '').toLowerCase() === 'personalise';
        } else {
          // no option named Embroidery — fallback to any option value === Personalise
          return variant.options.some(o => String(o).toLowerCase() === 'personalise');
        }
      }

      // Fallback: attempt to find the option input/label with name Embroidery on the page and see selected value
      const optionWrappers = document.querySelectorAll('.product-form__input, .product-option');
      for (const w of optionWrappers) {
        const label = w.querySelector('label, legend');
        const labelText = label ? label.textContent.trim().toLowerCase() : '';
        if (labelText.includes('embroidery')) {
          const sel = w.querySelector('select');
          if (sel) return String(sel.value).toLowerCase() === 'personalise';
          const checked = w.querySelector('input[type="radio"]:checked');
          if (checked) return String(checked.value).toLowerCase() === 'personalise';
        }
      }

      // As a last fallback, if any option value equals 'Personalise' then allow it.
      return variant.options.some(o => String(o).toLowerCase() === 'personalise');
    }
    return false;
  }

  // Build storage key
  function storageKey(productId, variantId) {
    return `${STORAGE_PREFIX}${productId || 'unknown'}::${variantId || 'novariant'}`;
  }

  // save monogram object: { text, confirmed }
  function saveMonogram(productId, variantId, payload) {
    try {
      localStorage.setItem(storageKey(productId, variantId), JSON.stringify(payload));
    } catch (e) {
      console.warn('monogram save failed', e);
    }
  }
  function loadMonogram(productId, variantId) {
    try {
      const raw = localStorage.getItem(storageKey(productId, variantId));
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }
  function deleteMonogram(productId, variantId) {
    try {
      localStorage.removeItem(storageKey(productId, variantId));
    } catch (e) {}
  }

  // Render Add/Edit/Delete controls
  function renderControls(container, productId, variantId) {
    container.innerHTML = ''; // clear
    const data = loadMonogram(productId, variantId);
    if (!data) {
      const addBtn = document.createElement('button');
      addBtn.type = 'button';
      addBtn.className = 'monogram-btn primary monogram-add-btn';
      addBtn.textContent = 'ADD MONOGRAM';
      addBtn.addEventListener('click', () => openMonogramPopup(productId, variantId));
      container.appendChild(addBtn);
      return;
    }

    // Show saved summary + Edit/Delete
    const summary = document.createElement('div');
    summary.className = 'monogram-small';
    summary.innerHTML = `<strong>Monogram:</strong> <span id="monogram-saved-text">${escapeHtml(data.text)}</span> ${data.confirmed ? ' (confirmed)' : ''}`;
    container.appendChild(summary);

    const actions = document.createElement('div');
    actions.className = 'monogram-edit-delete';
    const editBtn = document.createElement('button');
    editBtn.type = 'button';
    editBtn.className = 'monogram-btn';
    editBtn.textContent = 'Edit';
    editBtn.addEventListener('click', () => openMonogramPopup(productId, variantId, data));
    const deleteBtn = document.createElement('button');
    deleteBtn.type = 'button';
    deleteBtn.className = 'monogram-btn';
    deleteBtn.textContent = 'Delete';
    deleteBtn.addEventListener('click', () => {
      openDeleteConfirm(() => {
        // DELETE EXECUTE
        deleteMonogram(productId, variantId);

        const hidden = document.getElementById(HIDDEN_INPUT_ID);
        if (hidden) hidden.value = '';

        renderControls(container, productId, variantId);

        try {
          if (typeof updateMonogramUI === "function") updateMonogramUI();
          if (window.__monogramFeature?.update) window.__monogramFeature.update();
        } catch (e) {}
      });
    });

    actions.appendChild(editBtn);
    actions.appendChild(deleteBtn);
    container.appendChild(actions);
  }

  function openDeleteConfirm(onConfirm) {
    const backdrop = document.getElementById("monogram-delete-confirm-backdrop");
    const confirmBtn = document.getElementById("monogram-delete-confirm");
    const cancelBtn = document.getElementById("monogram-delete-cancel");

    backdrop.style.display = "flex";

    const close = () => backdrop.style.display = "none";

    cancelBtn.onclick = () => close();

    confirmBtn.onclick = () => {
      close();
      if (typeof onConfirm === "function") onConfirm();
    };

    // Clicking outside closes
    backdrop.addEventListener("click", function (e) {
      if (e.target === backdrop) close();
    });

    // ESC closes
    document.addEventListener("keydown", function esc(e) {
      if (e.key === "Escape") {
        close();
        document.removeEventListener("keydown", esc);
      }
    });
  }


  // Escape helper
  function escapeHtml(str) {
    if (!str) return '';
    return String(str).replace(/[&<>"']/g, s => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    })[s]);
  }

  // Build product image url to show in popup (best-effort)
  function getProductImageForPopup() {
    // try main product image element
    const firstImg = document.querySelector('.product__media img, .product-single__photo img, [data-media-id] img');
    if (firstImg && firstImg.src) return firstImg.src.replace("&width=128","");
    // fallback: find first image in gallery
    const galleryImg = document.querySelector('.product-media-gallery img');
    if (galleryImg && galleryImg.src) return galleryImg.src;
    // else blank
    return '';
  }

  // Create and open popup. If initialData passed then populate fields.
  function openMonogramPopup(productId, variantId, initialData = null) {
    // Build popup DOM
    const root = document.getElementById(POPUP_ROOT_ID) || document.createElement('div');
    root.id = POPUP_ROOT_ID;
    document.body.appendChild(root);

    // Remove existing backdrop if present
    const old = document.getElementById('monogram-popup-backdrop');
    if (old) old.remove();

    const backdrop = document.createElement('div');
    backdrop.id = 'monogram-popup-backdrop';
    backdrop.innerHTML = `
      <div id="monogram-popup" role="dialog" aria-modal="true" aria-label="Monogram">
        <div class="left">
          ${getProductImageForPopup() ? `<img src="${getProductImageForPopup()}" alt="Product image">` : `<div style="padding:18px;color:#666">No image available</div>`}
        </div>
        <div class="right">
          <div class="field">
            <h5>Make it Personal</h5>
            <p style="margin-top: 5px;font-size: 12px!important">
              Add the perfect finishing touch to your PLUSH Bridal Pyjamas with our beautiful embroidery service.
            </p>
          </div>
          <div class="field">
            <label for="monogram-text-input">Enter Personalisation Text (Max ${MAX_CHARS} characters)</label>
            <input id="monogram-text-input" class="monogram-text" type="text" maxlength="${MAX_CHARS}" placeholder="e.g. J.Doe" />
            </div>
            <div class="field">
            <label><input type="checkbox" id="monogram-confirm-checkbox"> Please note that personalised items are non-returnable unless faulty, so please double check the
            spelling before finalising your order.</label>
            <div id="monogram-error" style="color:#c00; font-size:13px; margin-top:6px; display:none;"></div>
          </div>
          <div class="actions">
            <button class="monogram-btn" id="monogram-cancel">Cancel</button>
            <button class="monogram-btn primary" id="monogram-save">Save</button>
          </div>
        </div>
      </div>
    `;
    root.appendChild(backdrop);

    // Focus/values
    const input = document.getElementById('monogram-text-input');
    const confirmCheckbox = document.getElementById('monogram-confirm-checkbox');
    if (initialData) {
      input.value = initialData.text || '';
      confirmCheckbox.checked = !!initialData.confirmed;
    } else {
      const existing = loadMonogram(productId, variantId);
      if (existing) {
        input.value = existing.text || '';
        confirmCheckbox.checked = !!existing.confirmed;
      } else {
        input.value = '';
        confirmCheckbox.checked = false;
      }
    }
    input.focus();

    // Handlers
    function closePopup() {
      backdrop.remove();
    }
    document.getElementById('monogram-cancel').addEventListener('click', closePopup);
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) closePopup();
    });
    document.addEventListener('keydown', function escHandler(e) {
      if (e.key === 'Escape') {
        closePopup();
        document.removeEventListener('keydown', escHandler);
      }
    });

    document.getElementById('monogram-save').addEventListener('click', () => {
      const text = (input.value || '').trim();
      const confirmed = !!confirmCheckbox.checked;
      const errorBox = document.getElementById("monogram-error");

      // Reset errors
      errorBox.style.display = "none";
      errorBox.textContent = "";

      // Validation
      if (!text) {
        errorBox.textContent = "Please enter your monogram text.";
        errorBox.style.display = "block";
        input.focus();
        return;
      }

      if (text.length > MAX_CHARS) {
        errorBox.textContent = "Monogram text cannot exceed " + MAX_CHARS + " characters.";
        errorBox.style.display = "block";
        input.focus();
        return;
      }

      if (!confirmed) {
        errorBox.textContent = "Please confirm your monogram is correct.";
        errorBox.style.display = "block";
        return;
      }

      // Save to localStorage
      const payload = { text, confirmed, savedAt: new Date().toISOString() };
      saveMonogram(productId, variantId, payload);

      // Update hidden input inside our monogram container
      const hidden = document.getElementById(HIDDEN_INPUT_ID);
      if (hidden) hidden.value = text;

      // Also ensure product form has hidden input for cart submission
      try {
        const productFormHidden = document.querySelector('form[action="/cart/add"] input[name="properties[Monogram]"]');
        if (productFormHidden) {
          productFormHidden.value = text;
        } else {
          const productForm = document.querySelector('product-form, form[action="/cart/add"], .product-form, form.product-form');
          if (productForm) {
            const newHidden = document.createElement('input');
            newHidden.type = 'hidden';
            newHidden.name = 'properties[Monogram]';
            newHidden.value = text;
            productForm.appendChild(newHidden);
          }
        }
      } catch (e) {}

      // Re-render controls UI
      const controlsNode = document.querySelector('#' + MONOGRAM_CONTAINER_ID + ' #monogram-controls') || document.getElementById(MONOGRAM_CONTAINER_ID);
      if (controlsNode) renderControls(controlsNode, productId, variantId);

      // Sync theme if needed
      try {
        if (typeof updateMonogramUI === "function") updateMonogramUI();
        if (window.__monogramFeature?.update) window.__monogramFeature.update();
      } catch (e) {}

      closePopup();
    });


  }

  // Main initialization / update function to be called on variant change and page load
  function updateMonogramUI() {
    // quick checks
    if (!productHasPersonalisedTag()) {
      // hide if previously visible
      const container = document.getElementById(MONOGRAM_CONTAINER_ID);
      if (container) container.style.display = 'none';
      return;
    }

    // ensure container exists
    let monogramContainer = document.getElementById(MONOGRAM_CONTAINER_ID);
    if (!monogramContainer) {
      monogramContainer = document.createElement('div');
      monogramContainer.id = MONOGRAM_CONTAINER_ID;
      monogramContainer.style.display = 'none';
      // hidden input (if not present) — include name as properties[Monogram] so it may be submitted to cart
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.id = HIDDEN_INPUT_ID;
      hidden.name = 'properties[Monogram]';
      hidden.value = '';
      monogramContainer.appendChild(hidden);

      const controls = document.createElement('div');
      controls.id = 'monogram-controls';
      monogramContainer.appendChild(controls);

      // Insert near product form if possible
      const productForm = document.querySelector('product-form, form[action="/cart/add"], .product-form');
      if (productForm) {
        productForm.parentNode.insertBefore(monogramContainer, productForm.nextSibling);
      } else {
        document.body.appendChild(monogramContainer);
      }
    }

    // get current variant
    const variant = getCurrentVariantFromDOM();
    const productId = (window.product && window.product.id) ? window.product.id : (document.body.dataset.productId || null);
    const variantId = variant?.id || null;

    // If embroidery personalized condition not satisfied hide
    if (!isEmbroideryPersonalised(variant)) {
      monogramContainer.style.display = 'none';
      return;
    }

    // show controls
    monogramContainer.style.display = 'block';
    const controlsContainer = monogramContainer.querySelector('#monogram-controls') || monogramContainer;
    renderControls(controlsContainer, productId, variantId);

    // update hidden input with saved value if exists
    const saved = loadMonogram(productId, variantId);
    const hiddenInput = document.getElementById(HIDDEN_INPUT_ID);
    if (hiddenInput) {
      hiddenInput.value = saved ? saved.text : '';
    }
  }

  // Attach listeners: on DOMContentLoaded and when variant selection changes
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(updateMonogramUI, 200); // slight delay to allow theme JS to render
  });

  // Many themes dispatch a custom event when variant changes or when product info updated.
  // We subscribe to common ones if present.
  window.addEventListener('product-info:loaded', updateMonogramUI);
  window.addEventListener('variant:change', updateMonogramUI);
  document.addEventListener('variant:change', updateMonogramUI);

  // Also listen to generic option change events: selects and radios inside product form
  document.addEventListener('change', (e) => {
    const el = e.target;
    if (!el) return;
    if (el.matches('.product-form__input select, .product-form__input input[type="radio"], select[name^="option"], input[name^="option"]')) {
      // small timeout allow theme to update internal state
      setTimeout(updateMonogramUI, 120);
    }
  });

  // If your theme publishes the optionValueSelectionChange event (your file does), we can listen for it safely
  try {
    if (typeof window !== 'undefined' && window.PUB_SUB_EVENTS && window.subscribe) {
      subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (payload) => {
        // payload may be present when user changes option
        setTimeout(updateMonogramUI, 80);
      });
    }
  } catch (e) { /* ignore if pubsub not present */ }

  // Expose function for debugging if needed
  window.__monogramFeature = {
    update: updateMonogramUI,
    open: openMonogramPopup,
    load: loadMonogram,
    save: saveMonogram,
    delete: deleteMonogram
  };

})();
